import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {SnippetShowPresenter} from './SnippetShowPresenter';
import {PresenterProps, withPresenter} from '../../Support/with_presenter';
import {ErrorPlaceholder} from '../Components/Utils/ErrorPlaceholder';
import {Loader} from '../Components/Utils/Loader';
import {NetworkRequestStatusView} from '../Components/Utils/NetworkRequestStatusView';
import {Feedback} from '../Components/Feedback';
import {format} from 'date-fns';
import {Bucket} from '../Components/Utils/Bucket';
import {formatDuration} from '../Support/Formatting/formatDuration';
import {formatDistance} from '../Support/Formatting/formatDistance';
import {formatSplit} from '../Support/Formatting/formatSplit';
import {Speed} from '../../Domain/Speed';
import {formatAverageStrokeLength} from '../Support/Formatting/formatStrokeLength';
import {formatAveragePower} from '../Support/Formatting/formatPower';
import {Link} from 'react-router-dom';
import {Graphs} from '../../Graphs/IndoorsGraphs';
import {StrokeAnalyzer} from '../Components/Workout/Indoor/StrokeAnalyzer/StrokeAnalyzer';
import {DownloadedIndoorWorkout} from '../../Models/DownloadedIndoorWorkout';
import {FullWorkoutSummary} from './Components/FullWorkoutSummary';
import {EditSnippet} from './Components/EditSnippet';
import {Csrf} from '../Components/Form/Csrf';

interface OwnProps {
    id: number;
}

function hasStrokes(downloadedIndoorWorkout: DownloadedIndoorWorkout): boolean {
    return downloadedIndoorWorkout.workoutIntervals.some((workoutInterval) => {
        return workoutInterval.strokes.length > 0;
    });
}

const SnippetShowComponent: FunctionComponent<OwnProps & PresenterProps<SnippetShowPresenter>> = observer(
    ({presenter}) => {
        if (presenter.result.status === 'loading') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Snippet</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Loader className="mt-5" />
                        </div>
                    </div>
                </div>
            );
        }

        if (presenter.result.status === 'error') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Snippet</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <ErrorPlaceholder large className="mt-5" />
                        </div>
                    </div>
                </div>
            );
        }

        const snippet = presenter.result.data;

        const highlightedRange = presenter.toHighlightedRange(snippet);

        return (
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col-12 d-flex flex-row justify-content-start">
                                <Link to="/reports" className="btn btn-back-arrow">
                                    <i className="bi bi-arrow-left"></i>
                                </Link>
                                <h1>Snippet</h1>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-md-9">
                                <div className="card">
                                    <div className="card-title">
                                        <h5 className="d-flex justify-content-between align-items-baseline flex-row flex-wrap">
                                            <span>Snippet</span>
                                            <span className="fs-6">
                                                {format(snippet.publicWorkout.executedAt, 'PPpp')}
                                            </span>
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row d-flex flex-column gap-2">
                                            <div className="col-12 d-flex flex-row gap-2 flex-wrap">
                                                <Bucket value={formatDuration(snippet.elapsedTime)} label="Duration" />
                                                <Bucket
                                                    value={formatDistance(snippet.traveledDistance)}
                                                    label="Distance"
                                                />
                                            </div>
                                            <div className="col-12 d-flex flex-row gap-2 flex-wrap">
                                                <Bucket
                                                    value={formatSplit(
                                                        Speed.fromDurationPer500Meters(snippet.averageSplit),
                                                    )}
                                                    label="Ø Split"
                                                />
                                                <Bucket
                                                    value={formatAverageStrokeLength(snippet.averageStrokeLength)}
                                                    label="Ø Stroke length"
                                                />
                                                <Bucket
                                                    value={formatAveragePower(snippet.averagePower)}
                                                    label="Ø Power"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <NetworkRequestStatusView
                                    result={presenter.downloadedIndoorWorkoutResult}
                                    loader={() => <Loader className="mt-3" />}
                                    error={() => null}
                                    component={(downloadedIndoorWorkout) =>
                                        hasStrokes(downloadedIndoorWorkout) && (
                                            <>
                                                <Graphs
                                                    className="mt-3"
                                                    range={highlightedRange}
                                                    downloadedIndoorWorkout={downloadedIndoorWorkout}
                                                />
                                                <StrokeAnalyzer
                                                    className="mt-3"
                                                    range={highlightedRange}
                                                    downloadedIndoorWorkout={downloadedIndoorWorkout}
                                                />
                                            </>
                                        )
                                    }
                                />
                            </div>
                            <div className="col-12 col-md-3">
                                <EditSnippet className="mb-3" snippet={snippet} />
                                <FullWorkoutSummary className="mb-3" fullWorkout={snippet.publicWorkout} />
                                <Feedback className="mb-3" />
                                <div className="row mb-3">
                                    <div className="col-12 text-center">
                                        <form
                                            action={`/reports/${snippet.id}/delete`}
                                            method="post"
                                            onSubmit={(e) => {
                                                if (!confirm('Are you sure you want to delete this snippet?')) {
                                                    e.preventDefault();
                                                    return false;
                                                }
                                            }}
                                        >
                                            <Csrf />
                                            <button className="btn">
                                                <i className="bi-trash me-2" />
                                                Delete snippet
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export const SnippetShow = withPresenter<SnippetShowPresenter, OwnProps>(
    (props, component) => new SnippetShowPresenter(props.id, component.network.snippets, component.network.strokes),
    SnippetShowComponent,
);
